import { BaseDto } from 'stores/types';
import { AbstractValidator } from 'fluent-ts-validator';
import i18n from 'i18n';
import { repository } from 'redux-scaffolding-ts';
import { DataStore } from 'stores/dataStore';
import HttpService from 'services/http-service';
import { container } from 'inversify.config';
import { ItemResult } from 'stores/types';
import * as FileSaver from 'file-saver';

export interface EventTypeChecklistTemplateDto extends BaseDto {
  id: string;
  eventTypeId: string;
  originalName: string;
  uploadedName: string;
}

export interface UpsertEventTypeChecklistTemplateDto {
  eventTypeId: string;
  originalName: string;
  uploadedName: string;
}

export class EventTypeChecklistTemplateValidator extends AbstractValidator<UpsertEventTypeChecklistTemplateDto> {
  constructor() {
    super();

    this.validateIfString(o => o.eventTypeId)
      .isNotEmpty()
      .isUuid('4')
      .withFailureMessage(String(i18n.t('Event Type Id is required')));

    this.validateIfString(o => o.originalName)
      .isNotEmpty()
      .withFailureMessage(String(i18n.t('Original file name is required')));

    this.validateIfString(o => o.uploadedName)
      .isNotEmpty()
      .withFailureMessage(String(i18n.t('Uploaded file name is required')));
  }
}

@repository('@@CHECKLIST_EVENTTYPE_TEMPLATE', 'checklist.eventtype.template')
export class EventTypeChecklistTemplateStore extends DataStore<EventTypeChecklistTemplateDto> {
  //baseUrl = 'http://localhost:7071/api/v1';
  baseUrl = 'events/v1';
  createPath = 'upsert-checklist-eventtype-template';
  retrievePath = 'get-checklist-eventtype-template';
  updatePath = 'upsert-checklist-eventtype-template';
  deletePath = 'delete-checklist-eventtype-template';

  protected validate(item: EventTypeChecklistTemplateDto) {
    return new EventTypeChecklistTemplateValidator().validate({
      eventTypeId: item.eventTypeId,
      originalName: item.originalName,
      uploadedName: item.uploadedName
    });
  }

  constructor() {
    super('CHECKLIST_EVENTTYPE_TEMPLATE', {
      isBusy: false,
      items: [],
      count: 0,
      result: undefined,
      discard: item => {}
    });
  }

  public async getById(id: string): Promise<EventTypeChecklistTemplateDto> {
    const httpService = container.get(HttpService);
    const result = await this.dispatchAsync(
      this.retrievePath,
      httpService.get<ItemResult<EventTypeChecklistTemplateDto>>(`${this.baseUrl}/${this.retrievePath}/${id}`)
    );
    return result.data.item;
  }

  public async getByEventTypeId(eventTypeId: string): Promise<EventTypeChecklistTemplateDto | null> {
    const result = await this.getById(eventTypeId);
    return result ?? null;
  }

  public async downloadByEventTypeId(eventTypeId: string, fileName?: string) {
    const template = await this.getByEventTypeId(eventTypeId);
    const httpService = container.get(HttpService);
    const result = await httpService.get<ItemResult<string>>(`${this.baseUrl}/download-checklist-template/${eventTypeId}`);
    if (!result.data.isSuccess || !result.data.item) {
      throw new Error('File download failed');
    }

    const response = await fetch(result.data.item);
    if (!response.ok) {
      throw new Error('File download failed');
    }

    const blob = await response.blob();
    FileSaver.saveAs(blob, fileName || template?.originalName || 'template.xlsx');
    return template;
  }
}
